import { useState } from 'react'
import { InfoTip } from '../components/InfoTip'
import { GreeksoftApiContent } from './GreeksoftApiKB'

/* ─────────────────────────────────────────────────────────────────
   KNOWLEDGE BASE — Multi-Strategy, Single-Page Layout
   Tabs select strategy. Content inside = full scrollable page.
   RSI Straddle content matches client-approved spec with 0% deviation.
   ───────────────────────────────────────────────────────────────── */

export function KnowledgeBase() {
  const [activeTab, setActiveTab] = useState(0)

  return (
    <div className="kb-page">
      <style>{kbStyles}</style>

      {/* ── Content ── */}
      <div className="kb-content">
        {strategies[activeTab].content}

        <div className="kb-footer">
          <p>Aalap Knowledge Base · Admin & Trader access</p>
        </div>
      </div>

      {/* ── Sidebar (right) ── */}
      <nav className="kb-sidebar">
        <div className="kb-sidebar-title">Strategy Knowledge Base</div>
        <div className="kb-sidebar-sub">{strategies.length} Documents</div>
        <div className="kb-sidebar-divider" />
        {strategies.map((s, i) => (
          <button
            key={s.id}
            className={`kb-tab ${activeTab === i ? 'active' : ''} ${s.badgeColor === 'muted' ? 'pending' : ''}`}
            onClick={() => setActiveTab(i)}
          >
            <span className="kb-tab-num">#{i + 1}</span>
            <span className="kb-tab-name">{s.name}</span>
            <span className={`kb-tab-badge ${s.badgeColor}`}>{s.badgeText}</span>
          </button>
        ))}
        <div className="kb-sidebar-divider" />
        <div className="kb-sidebar-footer">v2.0 · {new Date().toISOString().slice(0, 10)}</div>
      </nav>
    </div>
  )
}

/* ─── Strategy definitions with their full-page content ─── */

interface StrategyTab {
  id: string
  name: string
  badgeText: string
  badgeColor: string
  content: React.ReactNode
}

const strategies: StrategyTab[] = [
  {
    id: 'rsi-straddle',
    name: 'RSI Straddle',
    badgeText: 'All Days',
    badgeColor: 'green',
    content: <RsiStraddleContent />,
  },
  {
    id: 'trending-expiry',
    name: 'Trending Expiry',
    badgeText: 'Expiry Only',
    badgeColor: 'purple',
    content: <TrendingExpiryContent />,
  },
  {
    id: 'greeksoft-api',
    name: 'Greeksoft API',
    badgeText: 'Reference',
    badgeColor: 'blue',
    content: <GreeksoftApiContent />,
  },
  {
    id: 'faq',
    name: 'FAQ',
    badgeText: '32 Answered',
    badgeColor: 'gold',
    content: <FaqContent />,
  },
  {
    id: 'architecture',
    name: 'System Architecture',
    badgeText: 'Technical',
    badgeColor: 'orange',
    content: <SystemArchitectureContent />,
  },
]

/* ═══════════════════════════════════════════════════════════════════
   STRATEGY #1: RSI STRADDLE — Full Single-Page Content
   Content matches client-approved spec with 0% deviation.
   ═══════════════════════════════════════════════════════════════════ */

function RsiStraddleContent() {
  return (
    <>
      {/* Hero */}
      <div className="kb-hero">
        <div className="kb-hero-glow" />
        <div className="kb-hero-content">
          <div className="kb-badge-row">
            <span className="kb-badge blue">Strategy #1</span>
            <span className="kb-badge green">All Indices</span>
            <span className="kb-badge purple">VIX RSI Re-Entry</span>
          </div>
          <h2 className="kb-hero-title">RSI Straddle Strategy</h2>
          <p className="kb-hero-sub">Complete Specification</p>
        </div>
      </div>

      {/* Overview */}
      <section className="kb-sec">
        <div className="kb-sec-head">
          <span className="kb-sec-icon">📋</span>
          <h2>Overview</h2>
        </div>
        <div className="kb-prose">
          <p>
            Short ATM straddle at market open, managed with a premium-based trailing stop-loss. Re-entries governed by India VIX RSI conditions. Applicable on all indices, all trading days.
          </p>
          <div className="kb-meta-row">
            <div className="kb-meta-chip"><span className="kb-meta-k">Underlying</span><span className="kb-meta-v">All Indices — NIFTY, BANKNIFTY, FINNIFTY, SENSEX, BANKEX, MIDCAPNIFTY</span></div>
            <div className="kb-meta-chip"><span className="kb-meta-k">Applicability</span><span className="kb-meta-v">All trading days (designed for expiry days, deployable daily)</span></div>
            <div className="kb-meta-chip"><span className="kb-meta-k">Expiry Selection</span><span className="kb-meta-v">Nearest upcoming expiry (weekly or monthly, auto-selected per index)</span></div>
          </div>
        </div>
      </section>

      {/* Entry Rules */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🎯</span><h2>Entry Rules</h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">1</span>
            <div className="kb-rule-body">
              At <strong>9:30 AM IST</strong>, sell ATM Straddle (1 CE + 1 PE, same strike, nearest expiry).
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">2</span>
            <div className="kb-rule-body">
              Note the <strong>combined premium</strong> (CE + PE). This is the reference for SL and target.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">3</span>
            <div className="kb-rule-body">
              Set <strong>Stop Loss at 20% above entry premium</strong>. E.g., entry at ₹200 → SL at ₹240.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">4</span>
            <div className="kb-rule-body">
              <strong>Quantity (lots)</strong> is configurable. Default: <strong>1 lot</strong>.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">5</span>
            <div className="kb-rule-body">
              <strong>Order Type:</strong> Market or Limit (configurable). Applies to all orders in the strategy.
            </div>
          </div>
        </div>
      </section>

      {/* Trailing Stop-Loss Rules */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">📉</span><h2>Trailing Stop-Loss Rules <InfoTip term="slPrice" placement="right" /></h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">6</span>
            <div className="kb-rule-body">
              Every <strong>10-min candle close</strong>, if premium decreased → trail SL to 20% above new low. E.g., premium decays to ₹160 → new SL = ₹192.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">7</span>
            <div className="kb-rule-body">
              <strong>Minimum SL Rule:</strong> If premium {'<'} <strong>₹50</strong>, use fixed SL of <strong>₹10</strong> (not 20%). E.g., premium ₹35 → SL at ₹45.
            </div>
          </div>
          <div className="kb-formula-row">
            <div className="kb-formula-card">
              <div className="kb-formula-label">Premium ≥ ₹50</div>
              <div className="kb-formula-val">SL = Premium × 1.20</div>
              <div className="kb-formula-sub">20% trailing stop-loss</div>
            </div>
            <div className="kb-formula-card warn">
              <div className="kb-formula-label">Premium {'<'} ₹50</div>
              <div className="kb-formula-val">SL = Premium + ₹10</div>
              <div className="kb-formula-sub">Fixed minimum cushion</div>
            </div>
          </div>
        </div>
      </section>

      {/* Re-Entry Rules */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🔄</span><h2>Re-Entry Rules <InfoTip term="vixRsi" placement="right" /></h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">8</span>
            <div className="kb-rule-body">
              On SL exit, check <strong>14-period RSI of India VIX (3-min chart)</strong>:
              <div className="kb-decision-grid">
                <div className="kb-decision-card green">
                  <div className="kb-decision-header">VIX RSI {'<'} 60</div>
                  <div className="kb-decision-body">
                    Re-enter immediately — volatility is not elevated.
                  </div>
                </div>
                <div className="kb-decision-card red">
                  <div className="kb-decision-header">VIX RSI ≥ 60</div>
                  <div className="kb-decision-body">
                    Wait for RSI to cool below <strong>55</strong> before re-entering.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">9</span>
            <div className="kb-rule-body">
              Re-entry follows <strong>same rules</strong> — new premium, new 20% trailing SL, same minimum SL rule.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">10</span>
            <div className="kb-rule-body">
              <strong>Max 3 total entries</strong> per day (1 original + 2 re-entries).
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">11</span>
            <div className="kb-rule-body">
              <strong>No re-entry after 2:30 PM IST</strong>, regardless of VIX RSI conditions.
            </div>
          </div>
          <div className="kb-flow">
            <div className="kb-flow-step exit">SL Hit → Exit</div>
            <div className="kb-flow-arrow">→</div>
            <div className="kb-flow-step">Count {'<'} 3?</div>
            <div className="kb-flow-arrow">→</div>
            <div className="kb-flow-step">Before 2:30?</div>
            <div className="kb-flow-arrow">→</div>
            <div className="kb-flow-step accent">VIX RSI Check</div>
            <div className="kb-flow-arrow">→</div>
            <div className="kb-flow-step green">Re-Enter ATM</div>
          </div>
        </div>
      </section>

      {/* Exit Rules */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🚪</span><h2>Exit Rules</h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">12</span>
            <div className="kb-rule-body">
              <strong>Target:</strong> Exit when <strong>40% profit received</strong> (premium decays 40%). E.g., ₹200 entry → exit at ₹120.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">13</span>
            <div className="kb-rule-body">
              <strong>SL Exit:</strong> Exit when premium rises above current trailing SL level.
            </div>
          </div>
        </div>
      </section>

      {/* Event Day Calendar */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">📅</span><h2>Event Day Calendar</h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">14</span>
            <div className="kb-rule-body">
              <strong>No trading on event days</strong> (RBI policy, Budget, elections, etc.).
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">15</span>
            <div className="kb-rule-body">
              Admin marks "no trade" dates via <strong>calendar page</strong>. System blocks auto-trades; traders can still manually override.
            </div>
          </div>
        </div>
      </section>

      {/* End-of-Day Square-Off */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">⏹</span><h2>End-of-Day Square-Off</h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">16</span>
            <div className="kb-rule-body">
              All open positions squared off at <strong>3:20 PM IST</strong> (configurable by admin).
            </div>
          </div>
        </div>
      </section>

      {/* Global Circuit Breaker */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🛡</span><h2>Global Circuit Breaker</h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">17</span>
            <div className="kb-rule-body">
              <strong>Global Breaker (3%):</strong> Combined loss across <strong>all strategies</strong> exceeds 3% of total margin → halt all, square off everything.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">18</span>
            <div className="kb-rule-body">
              On trigger: admin popup — <strong>"Continue or Stop for Day."</strong> All traders on hold until admin responds.
            </div>
          </div>
        </div>
      </section>

      {/* Summary */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">📊</span><h2>Summary of Key Parameters</h2></div>
        <div className="kb-summary-grid">
          <SummaryCard label="Entry Time" value="9:30 AM IST" icon="⏰" />
          <SummaryCard label="Position" value="Short ATM Straddle" sub="Sell CE + sell PE, same strike" icon="⚖" />
          <SummaryCard label="ATM Determination" value="At Spot price" icon="📍" />
          <SummaryCard label="Underlying" value="NIFTY, BANKNIFTY, FINNIFTY, SENSEX, BANKEX, MIDCAPNIFTY" icon="📈" />
          <SummaryCard label="Expiry" value="Nearest upcoming" sub="Weekly or monthly" icon="📆" />
          <SummaryCard label="Order Type" value="Configurable" sub="Market Order or Limit Order" icon="📝" />
          <SummaryCard label="Quantity" value="Configurable" sub="Default 1 lot" icon="📊" />
          <SummaryCard label="Stop-Loss" value="20% Trailing" sub="Recalculated every 10-min candle close" icon="🛡" />
          <SummaryCard label="Minimum SL" value="₹10 Fixed" sub="When premium falls below ₹50" icon="⚠" />
          <SummaryCard label="Profit Target" value="40% Decay" sub="40% of premium amount received" icon="🎯" />
          <SummaryCard label="Re-Entry" value="VIX RSI < 60" sub="14-period on 3-min chart; cooldown < 55" icon="📉" />
          <SummaryCard label="Max Entries" value="3" sub="1 initial + 2 re-entries" icon="🔢" />
          <SummaryCard label="No Re-Entry After" value="2:30 PM IST" icon="🕑" />
          <SummaryCard label="EOD Square-Off" value="3:20 PM IST" sub="Configurable from admin" icon="⏹" />
          <SummaryCard label="Global Breaker" value="3% Margin" sub="ALL running strategies" icon="🚨" />
          <SummaryCard label="Breaker Action" value="Admin Popup" sub="Continue or Stop for Day" icon="⚡" />
          <SummaryCard label="Event Day" value="Calendar page" sub="System blocks on marked dates" icon="📅" />
        </div>
      </section>

    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════
   STRATEGY #2: TRENDING EXPIRY — Placeholder
   Full spec to be added when client provides approved document.
   ═══════════════════════════════════════════════════════════════════ */

function TrendingExpiryContent() {
  return (
    <>
      {/* Hero */}
      <div className="kb-hero">
        <div className="kb-hero-glow" />
        <div className="kb-hero-content">
          <div className="kb-badge-row">
            <span className="kb-badge purple">Strategy #2</span>
            <span className="kb-badge blue">Expiry Days Only</span>
            <span className="kb-badge green">Multi-Set · 30-Min Interval</span>
          </div>
          <h2 className="kb-hero-title">Trending Expiry Strategy</h2>
          <p className="kb-hero-sub">Complete Specification</p>
        </div>
      </div>

      {/* Overview */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">📋</span><h2>Overview</h2></div>
        <div className="kb-prose">
          <p>
            Expiry-day straddle selling every 30 minutes. Each set managed independently with asymmetric SL. Adjustment legs on SL triggers.
          </p>
          <div className="kb-meta-row">
            <div className="kb-meta-chip"><span className="kb-meta-k">Underlying</span><span className="kb-meta-v">All Indices — NIFTY, BANKNIFTY, FINNIFTY, SENSEX, BANKEX, MIDCAPNIFTY</span></div>
            <div className="kb-meta-chip"><span className="kb-meta-k">Applicability</span><span className="kb-meta-v">Expiry days only</span></div>
            <div className="kb-meta-chip"><span className="kb-meta-k">Expiry Selection</span><span className="kb-meta-v">Nearest upcoming expiry (weekly or monthly, auto-selected per index)</span></div>
          </div>
        </div>
      </section>

      {/* Entry Rules */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🎯</span><h2>Entry Rules</h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">1</span>
            <div className="kb-rule-body">
              On <strong>expiry day</strong>, sell ATM Straddle (at synthetic price) every <strong>30 minutes</strong> from 9:30 AM to 2:30 PM IST (configurable).
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">2</span>
            <div className="kb-rule-body">
              Each 30-min entry creates a new <strong>independent set</strong> — own legs, SLs, adjustments, and P&L.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">3</span>
            <div className="kb-rule-body">
              Up to <strong>11 sets/day</strong> (9:30, 10:00, 10:30 … 2:00, 2:30).
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">4</span>
            <div className="kb-rule-body">
              <strong>Quantity (lots)</strong> is configurable per leg. Default: <strong>1 lot</strong>.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">5</span>
            <div className="kb-rule-body">
              <strong>Order Type:</strong> Market or Limit (configurable). Applies to all orders in the strategy.
            </div>
          </div>
        </div>
      </section>

      {/* Exit Rules */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🚪</span><h2>Exit Rules (Per Set — Separate for Call and Put)</h2></div>
        <div className="kb-prose">
          <p>Each leg managed independently, checked <strong>every second</strong>:</p>
          <div className="kb-rule">
            <span className="kb-rule-num">6</span>
            <div className="kb-rule-body">
              <strong>Put SL: 70%</strong> — put premium rises 70% from entry → square off. Trigger: Entry × 1.70.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">7</span>
            <div className="kb-rule-body">
              <strong>Call SL: 80%</strong> — call premium rises 80% from entry → square off. Trigger: Entry × 1.80.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">8</span>
            <div className="kb-rule-body">
              <strong>Target: 90% decay</strong> — per leg, per contract. Trigger: Entry × 0.10.
            </div>
          </div>
          <div className="kb-formula-row">
            <div className="kb-formula-card warn">
              <div className="kb-formula-label">Put SL</div>
              <div className="kb-formula-val">Entry × 1.70</div>
              <div className="kb-formula-sub">70% rise from entry premium</div>
            </div>
            <div className="kb-formula-card warn">
              <div className="kb-formula-label">Call SL</div>
              <div className="kb-formula-val">Entry × 1.80</div>
              <div className="kb-formula-sub">80% rise from entry premium</div>
            </div>
            <div className="kb-formula-card">
              <div className="kb-formula-label">Target</div>
              <div className="kb-formula-val">Entry × 0.10</div>
              <div className="kb-formula-sub">90% decay, per leg, per contract</div>
            </div>
          </div>
        </div>
      </section>

      {/* Adjustment Leg Rules */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🔄</span><h2>Adjustment Leg Rules</h2></div>
        <div className="kb-prose">
          <p>On primary leg SL hit → adjustment leg on <strong>opposite side</strong>, 1 strike OTM:</p>
          <div className="kb-rule">
            <span className="kb-rule-num">9</span>
            <div className="kb-rule-body">
              <strong>Put SL hit:</strong> Square off put → short extra <strong>Call at 1 strike OTM</strong>.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">10</span>
            <div className="kb-rule-body">
              <strong>Call SL hit:</strong> Square off call → short extra <strong>Put at 1 strike OTM</strong>.
            </div>
          </div>
          <div className="kb-decision-grid">
            <div className="kb-decision-card red">
              <div className="kb-decision-header">Put SL Hit</div>
              <div className="kb-decision-body">
                Square off Put → Short extra <strong>Call</strong> at 1 strike OTM<br/>
                SL: 50% · Target: 90%
              </div>
            </div>
            <div className="kb-decision-card red">
              <div className="kb-decision-header">Call SL Hit</div>
              <div className="kb-decision-body">
                Square off Call → Short extra <strong>Put</strong> at 1 strike OTM<br/>
                SL: 50% · Target: 90%
              </div>
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">11</span>
            <div className="kb-rule-body">
              Adjustment leg risk: <strong>SL 50%</strong> (Entry × 1.50) · <strong>Target 90%</strong> decay.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">12</span>
            <div className="kb-rule-body">
              <strong>Max 1 adjustment per side per set.</strong> Once adjustment SL is hit, no further position on that side.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">13</span>
            <div className="kb-rule-body">
              Adjustment logic applies <strong>independently to every 30-min set</strong>.
            </div>
          </div>
        </div>
      </section>

      {/* Circuit Breakers */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🛡</span><h2>Circuit Breaker Rules</h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">14</span>
            <div className="kb-rule-body">
              <strong>Per-Set Breaker (2%):</strong> Set loss exceeds 2% of set margin → square off entire set (incl. adjustments). Margin from broker API.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">15</span>
            <div className="kb-rule-body">
              <strong>Global Breaker (3%):</strong> Combined loss across <strong>all strategies</strong> exceeds 3% of total margin → halt all, square off everything. Admin popup: <strong>"Continue or Stop for Day."</strong>
            </div>
          </div>
          <div className="kb-formula-row">
            <div className="kb-formula-card warn">
              <div className="kb-formula-label">Per-Set Limit</div>
              <div className="kb-formula-val">2% of Set Margin</div>
              <div className="kb-formula-sub">Strategy-specific, per straddle set</div>
            </div>
            <div className="kb-formula-card warn">
              <div className="kb-formula-label">Global Limit</div>
              <div className="kb-formula-val">3% of Total Margin</div>
              <div className="kb-formula-sub">Across ALL running strategies</div>
            </div>
          </div>
        </div>
      </section>

      {/* End-of-Day Square-Off */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">⏱</span><h2>End-of-Day Square-Off</h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">16</span>
            <div className="kb-rule-body">
              All open positions squared off at <strong>3:20 PM IST</strong> (configurable by admin).
            </div>
          </div>
        </div>
      </section>

      {/* Event Day Calendar */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">📅</span><h2>Event Day Calendar</h2></div>
        <div className="kb-prose">
          <div className="kb-rule">
            <span className="kb-rule-num">17</span>
            <div className="kb-rule-body">
              Admin marks "no trade" dates via <strong>calendar page</strong>. System blocks auto-trades; traders can still manually override.
            </div>
          </div>
        </div>
      </section>

      {/* Configurable Parameters */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🔧</span><h2>Configurable Parameters (Manual Control)</h2></div>
        <div className="kb-prose">
          <p>The following variables are available for manual control if required:</p>
          <div className="kb-rule">
            <span className="kb-rule-num">18</span>
            <div className="kb-rule-body">
              <strong>Time Parameters</strong> — configurable start time and end time for the entry window (default: 9:30 AM to 2:30 PM IST).
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">19</span>
            <div className="kb-rule-body">
              <strong>Quantity Selection</strong> — configurable number of lots per leg for each straddle set. Default: 1 lot. Admin sets default; trader can override.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">20</span>
            <div className="kb-rule-body">
              <strong>Strike Selection</strong> — ability to choose the strike manually or by mode (ATM, ITM, OTM).
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">21</span>
            <div className="kb-rule-body">
              <strong>Stop-Loss Parameter</strong> — ability to adjust stop-loss percentages for calls, puts, and adjustment legs.
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">22</span>
            <div className="kb-rule-body">
              <strong>Delta Selection</strong> — strike selection by delta as a parameter. The system should support selection
              by either <strong>delta or strike</strong> (ATM / ITM / OTM).
            </div>
          </div>
          <div className="kb-rule">
            <span className="kb-rule-num">23</span>
            <div className="kb-rule-body">
              <strong>EOD Square-Off Time</strong> — configurable from admin login (default: 3:20 PM IST).
            </div>
          </div>
        </div>
      </section>

      {/* Summary */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">📊</span><h2>Summary of Key Parameters</h2></div>
        <div className="kb-summary-grid">
          <SummaryCard label="Applicability" value="Expiry Days" icon="📅" />
          <SummaryCard label="Underlying" value="All Indices" sub="NIFTY, BANKNIFTY, FINNIFTY, SENSEX, BANKEX, MIDCAPNIFTY" icon="📈" />
          <SummaryCard label="Expiry" value="Nearest Upcoming" sub="Weekly or monthly" icon="🗓" />
          <SummaryCard label="Entry Schedule" value="Every 30 min" sub="9:30 AM – 2:30 PM (configurable)" icon="⏰" />
          <SummaryCard label="Position" value="ATM at Synthetic" sub="Sell CE + sell PE, same strike" icon="⚖" />
          <SummaryCard label="Order Type" value="Configurable" sub="Market Order or Limit Order" icon="📝" />
          <SummaryCard label="Quantity" value="Configurable" sub="Lots per leg, default 1 lot" icon="📊" />
          <SummaryCard label="Max Sets/Day" value="11" icon="🔢" />
          <SummaryCard label="Put SL" value="70% Rise" sub="Entry × 1.70" icon="🔴" />
          <SummaryCard label="Call SL" value="80% Rise" sub="Entry × 1.80" icon="🔴" />
          <SummaryCard label="Target" value="90% Decay" sub="Entry × 0.10, per leg" icon="🎯" />
          <SummaryCard label="SL/Target Check" value="Every Second" sub="1-second monitoring" icon="⏱" />
          <SummaryCard label="Adjustment" value="Opposite Side" sub="1 strike OTM, once per side" icon="🔄" />
          <SummaryCard label="Adj. SL" value="50% Rise" sub="Entry × 1.50" icon="⚡" />
          <SummaryCard label="Adj. Target" value="90% Decay" icon="⚡" />
          <SummaryCard label="Set Breaker" value="2% Margin" sub="Margin from broker API, per set" icon="🛡" />
          <SummaryCard label="Global Breaker" value="3% Margin" sub="ALL running strategies" icon="🚨" />
          <SummaryCard label="Global Breaker Action" value="Admin Popup" sub="Continue or Stop for Day. All traders on hold" icon="🔔" />
          <SummaryCard label="EOD Square-Off" value="3:20 PM IST" sub="Configurable from admin" icon="⏱" />
          <SummaryCard label="Manual Controls" value="Time, Qty, Strike, SL, Delta, EOD" icon="🔧" />
        </div>
      </section>

    </>
  )
}

/* ── Summary Card ── */
function SummaryCard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon: string }) {
  return (
    <div className="kb-summary-card">
      <div className="kb-summary-icon">{icon}</div>
      <div className="kb-summary-label">{label}</div>
      <div className="kb-summary-value">{value}</div>
      {sub && <div className="kb-summary-sub">{sub}</div>}
    </div>
  )
}

/* ── Strategy Placeholder (for pending specs) ── */
function StrategyPlaceholder({ num, name }: { num: number; name: string }) {
  return (
    <>
      <div className="kb-hero">
        <div className="kb-hero-glow" />
        <div className="kb-hero-content">
          <div className="kb-badge-row">
            <span className="kb-badge blue">Strategy #{num}</span>
            <span className="kb-badge muted">Pending Specification</span>
          </div>
          <h2 className="kb-hero-title">{name}</h2>
          <p className="kb-hero-sub">Awaiting Client Specification</p>
        </div>
      </div>
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">📝</span><h2>Status</h2></div>
        <div className="kb-prose">
          <div className="kb-placeholder">
            <span className="kb-placeholder-icon">📋</span>
            <p><strong>Full specification pending.</strong></p>
            <p>The complete strategy document will be added once the client-approved specification is received.
            No partial or assumed rules will be documented — 0% deviation policy applies.</p>
          </div>
        </div>
      </section>
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════
   FAQ — All Client-Confirmed Q&A in FAQ Format
   Source: client_questions_status.md (June 2026)
   ═══════════════════════════════════════════════════════════════════ */

function FaqItem({ q, a, tag }: { q: string; a: React.ReactNode; tag?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="faq-item" onClick={() => setOpen(o => !o)}>
      <div className="faq-q">
        <span className="faq-icon">{open ? '▾' : '▸'}</span>
        <span>{q}</span>
        {tag && <span className="faq-tag">{tag}</span>}
      </div>
      {open && <div className="faq-a" onClick={e => e.stopPropagation()}>{a}</div>}
    </div>
  )
}

function FaqSection({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <section className="kb-sec" style={{ marginBottom: '0.75rem' }}>
      <div className="kb-sec-head">
        <span className="kb-sec-icon">{icon}</span>
        <h2>{title}</h2>
      </div>
      <div style={{ padding: '0.5rem 0' }}>{children}</div>
    </section>
  )
}

function FaqContent() {
  return (
    <>
      {/* Hero */}
      <div className="kb-hero">
        <div className="kb-hero-glow" />
        <div className="kb-hero-content">
          <div className="kb-badge-row">
            <span className="kb-badge blue">Platform FAQ</span>
            <span className="kb-badge green">32 Answered</span>
            <span className="kb-badge purple">June 2026</span>
          </div>
          <h2 className="kb-hero-title">Frequently Asked Questions</h2>
          <p className="kb-hero-sub">All client-confirmed decisions — trading setup, execution, risk, margin & system architecture</p>
        </div>
      </div>

      {/* ── SECTION A: Trading Setup ── */}
      <FaqSection icon="⚙️" title="Trading Setup & Configuration">
        <FaqItem
          q="How is the ATM (At-The-Money) strike determined?"
          tag="ATM Strike"
          a={<>
            <p>It depends on the strategy:</p>
            <ul>
              <li><strong>RSI Straddle</strong> — ATM is determined by the <strong>Spot Price</strong> of the index (closest strike to current spot).</li>
              <li><strong>Trending Expiry</strong> — ATM is determined by the <strong>Synthetic Futures Price</strong> (calculated via Call − Put + Strike = Synthetic Futures).</li>
            </ul>
            <p>ATM strike is pulled directly from the broker API wherever supported.</p>
          </>}
        />
        <FaqItem
          q="Which indices does Aalap support?"
          tag="Indices"
          a={<p>All six major NSE/BSE indices: <strong>NIFTY, BANKNIFTY, FINNIFTY, SENSEX, BANKEX,</strong> and <strong>MIDCAPNIFTY</strong>. Some have weekly expiry, some have only monthly expiry — the system handles both automatically.</p>}
        />
        <FaqItem
          q="How is the expiry selected — weekly or monthly?"
          tag="Expiry"
          a={<>
            <p>Always the <strong>nearest upcoming expiry</strong>. The system checks today's date and selects whichever expiry date is closest (including today itself on expiry day).</p>
            <p>The nearest expiry is shown as the <strong>default</strong> in the UI. Traders can override manually.</p>
          </>}
        />
        <FaqItem
          q="What is the default lot size and can it be changed?"
          tag="Lot Size"
          a={<>
            <p>Default is <strong>1 lot</strong>. The admin sets this default for all traders. Individual traders can override it from their settings. Once saved, it persists until manually changed again.</p>
          </>}
        />
        <FaqItem
          q="What order type is used — market or limit?"
          tag="Order Type"
          a={<>
            <p><strong>Limit Order is the default</strong> because the exchange (NSE/BSE) does not allow pure market orders for options in most scenarios.</p>
            <ul>
              <li><strong>Limit order with 5% buffer</strong> — the system places limit orders at LTP + 5% to simulate market-order speed while staying within exchange rules.</li>
              <li><strong>Market order</strong> — can only be enabled with explicit approval from Aalap admin. It is not available by default.</li>
            </ul>
            <p>The order type is configurable from Admin Settings.</p>
          </>}
        />
        <FaqItem
          q="What is the end-of-day square-off time?"
          tag="EOD"
          a={<p>All open positions are forcefully squared off at <strong>3:20 PM IST</strong>. This time is configurable from the admin login — the admin can change it from Admin Settings.</p>}
        />
        <FaqItem
          q="Does the system support paper trading / simulation mode?"
          tag="Paper Trading"
          a={<p><strong>Yes.</strong> A paper trading mode is available where real market data is used but no actual orders are placed. All trades are logged as if they were real, allowing thorough testing before going live.</p>}
        />
      </FaqSection>

      {/* ── SECTION B: RSI Straddle Strategy ── */}
      <FaqSection icon="📉" title="RSI Straddle — Strategy Questions">
        <FaqItem
          q="Is MTM (Mark-to-Market) tracked leg-wise or as a combined straddle?"
          tag="MTM Mode"
          a={<>
            <p>Both modes are supported — <strong>setting-based</strong>. The admin configures the default:</p>
            <ul>
              <li><strong>Combined MTM</strong> — Call + Put premium tracked as one combined number.</li>
              <li><strong>Leg-wise MTM</strong> — Call and Put are tracked independently.</li>
            </ul>
            <p>This setting applies to both profit targets and stop-loss calculations.</p>
          </>}
        />
        <FaqItem
          q="How does the trailing stop-loss work and is it from the API or self-calculated?"
          tag="Trailing SL"
          a={<>
            <p>The trailing SL is recalculated at every <strong>10-minute candle close</strong>. If the straddle premium has decreased, the SL is trailed down to 20% of the new lower premium.</p>
            <p><strong>API first</strong> — the system first checks if the broker API provides a trailing stop-loss facility. If yes, the API output is used directly. If not, it is calculated from the straddle premium candle internally.</p>
          </>}
        />
        <FaqItem
          q="How is the stop-loss monitored — every tick or only at candle close?"
          tag="SL Monitoring"
          a={<p>The SL level is <strong>set at candle close</strong> (every 10 minutes) but <strong>monitored real-time tick-by-tick</strong> between candle closes. The breach is detected the moment the premium crosses the SL level, using the API's real-time WebSocket feed.</p>}
        />
        <FaqItem
          q="Is the profit target per-leg or combined?"
          tag="Profit Target"
          a={<p>Also <strong>setting-based</strong> — same as MTM mode. Admin configures whether the 40% profit target applies to the combined straddle premium or to each leg independently.</p>}
        />
      </FaqSection>

      {/* ── SECTION C: Trending Expiry Strategy ── */}
      <FaqSection icon="🔄" title="Trending Expiry — Strategy Questions">
        <FaqItem
          q="How many straddle sets are created per day?"
          tag="Entry Count"
          a={<p><strong>11 entries</strong> per day — from 9:30 AM to 2:30 PM at 30-minute intervals (9:30, 10:00, 10:30 ... 2:00, 2:30). Each creates an independent straddle set with its own P&L and SL tracking.</p>}
        />
        <FaqItem
          q="What price is used for stop-loss calculation — entry LTP or traded price?"
          tag="SL Basis · C2"
          a={<>
            <p>Stop-loss is based on the <strong>actual traded price</strong> (confirmed fill price), not the entry LTP at the time of order placement. Formulas:</p>
            <ul>
              <li>Put SL: Actual Traded Price × 1.70 (70% rise)</li>
              <li>Call SL: Actual Traded Price × 1.80 (80% rise)</li>
              <li>Adjustment Leg SL: Actual Traded Price × 1.50 (50% rise)</li>
            </ul>
          </>}
        />
        <FaqItem
          q="What is the quantity for adjustment legs?"
          tag="Adj. Qty · C3"
          a={<p><strong>Same quantity as the original leg</strong> by default. If the original straddle used 2 lots, the adjustment leg also uses 2 lots. This is configurable if needed.</p>}
        />
        <FaqItem
          q="How often are stop-loss and target levels checked?"
          tag="Monitoring · C5"
          a={<p>SL and target levels are checked <strong>every second</strong> via the API's real-time WebSocket feed. The system displays the API output directly — it does not independently calculate these values.</p>}
        />
        <FaqItem
          q="Can both Call SL and Put SL fire in the same set? Can both adjustment legs co-exist?"
          tag="Both Legs · C7"
          a={<>
            <p><strong>Yes — both adjustment legs can co-exist simultaneously</strong> within the same straddle set. They are fully independent of each other.</p>
            <ul>
              <li>If Put SL hits → extra Call is shorted. This extra call remains active even if the original call's SL fires later.</li>
              <li>If Call SL hits → extra Put is shorted. Both the extra call and extra put can be open at the same time in the same set.</li>
            </ul>
          </>}
        />
      </FaqSection>

      {/* ── SECTION D: Order Execution ── */}
      <FaqSection icon="📋" title="Order Execution & Brokers">
        <FaqItem
          q="Can orders be sent to two brokers simultaneously?"
          tag="Multi-Broker · D1"
          a={<>
            <p>The design requires orders to go to two brokers simultaneously (same strategy, different accounts). The feasibility depends on API support — this is being verified in the Greeksoft API documentation.</p>
            <p>Each broker account is an <strong>independent instance</strong> with separate P&L, separate SL tracking, and separate margin.</p>
          </>}
        />
        <FaqItem
          q="How is slippage handled?"
          tag="Slippage · D2"
          a={<>
            <p>Slippage handling is <strong>fully configurable</strong> from Admin Settings. The buffer can be set as:</p>
            <ul>
              <li>Fixed amount: ₹2, ₹5, or any value</li>
              <li>Percentage: 5% or any %</li>
            </ul>
            <p>This applies to limit order price buffers to account for bid-ask spread. Not hardcoded.</p>
          </>}
        />
        <FaqItem
          q="What happens if an order is only partially filled?"
          tag="Partial Fills · D3"
          a={<>
            <p>The system retries based on what the broker API allows. The number of retry attempts is:</p>
            <ul>
              <li>Determined from the API documentation (what the broker's API supports)</li>
              <li>Made <strong>configurable</strong> from Admin Settings</li>
            </ul>
            <p>Currently being verified against the Greeksoft API partial fill handling documentation.</p>
          </>}
        />
        <FaqItem
          q="Where are orders logged and what is shown in the Trades section?"
          tag="Order Logging · D6"
          a={<p>All orders (entry, exit, SL, adjustment, re-entry) are logged. The Trades and Orders section displays <strong>directly from the broker API output</strong> — the system does not calculate or infer trade data independently. What the API returns is what is shown.</p>}
        />
      </FaqSection>

      {/* ── SECTION E: Risk & Margin ── */}
      <FaqSection icon="🛡️" title="Risk Management & Margin">
        <FaqItem
          q="How is the global circuit breaker triggered?"
          tag="Circuit Breaker"
          a={<>
            <p>The global circuit breaker triggers when the combined net loss across ALL active strategies exceeds <strong>3% of the allotment margin</strong> for that trader for that day.</p>
            <ul>
              <li><strong>Allotment margin</strong> = the margin allotted to the trader at the start of the day (e.g., if ₹1 crore is allotted, the 3% trigger = ₹3 lakhs loss).</li>
              <li>The 3% threshold is <strong>configurable</strong> from Admin Settings.</li>
            </ul>
            <p>When triggered: a popup appears asking admin <em>"Continue with New Entries or Stop for the Day."</em> All traders are placed on hold until admin responds. Traders also see a popup telling them to contact admin.</p>
          </>}
        />
        <FaqItem
          q="What is the maximum loss allowed per day?"
          tag="Max Loss · E4"
          a={<p><strong>3% of allotment margin</strong> by default. This is the same threshold as the circuit breaker. The percentage is <strong>configurable</strong> from Admin Settings — the admin can set it to any value (e.g., 2%, 5%).</p>}
        />
        <FaqItem
          q="How is margin displayed — allotted, utilised, and available?"
          tag="Margin Display · E1"
          a={<>
            <p>The margin dashboard shows three values, all pulled <strong>directly from the broker API</strong>:</p>
            <ul>
              <li><strong>Margin Allotted</strong> — total margin given to the trader for the day</li>
              <li><strong>Margin Utilised</strong> — currently used by open positions</li>
              <li><strong>Margin Pending</strong> — available balance</li>
            </ul>
            <p>The system does not calculate margin independently. All values come from API only.</p>
          </>}
        />
        <FaqItem
          q="Which margin type is used — NRML (overnight) or MIS (intraday)?"
          tag="Margin Type · E3"
          a={<p>The system displays exactly what the broker API returns — <strong>NRML or MIS/Intraday</strong>, as provided. No override or independent calculation. Since both strategies are intraday, the broker will typically return intraday margin values during market hours.</p>}
        />
        <FaqItem
          q="Does the system check margin availability before placing an order?"
          tag="Margin Check · E1"
          a={<p>Margin availability is fetched from the broker API before order placement. The margin allotted, utilised, and pending values are all sourced from the API. The system uses these API-provided values to determine if sufficient margin exists before executing orders.</p>}
        />
      </FaqSection>

      {/* ── SECTION F: System & Notifications ── */}
      <FaqSection icon="🔧" title="System Architecture & Notifications">
        <FaqItem
          q="Where does the trading engine run?"
          tag="Architecture · F1"
          a={<>
            <p>Aalap uses a two-layer architecture:</p>
            <ul>
              <li><strong>VPS (tunnel.la-money.co.in)</strong> — Node.js trading engine that connects to broker WebSockets (IRIS/Apollo), executes strategy logic, monitors SL/targets, and places orders.</li>
              <li><strong>Cloudflare Workers (lamoney-api)</strong> — API layer for authentication, D1 database, broker management, and user management.</li>
              <li><strong>Cloudflare Pages (la-money.pages.dev)</strong> — React frontend for admin and traders.</li>
            </ul>
          </>}
        />
        <FaqItem
          q="How are notifications sent when SL hits or circuit breaker triggers?"
          tag="Notifications · F4"
          a={<>
            <p>Two channels are used — both are free:</p>
            <ul>
              <li><strong>Browser Push Notifications (Web Push / PWA)</strong> — works even when the browser tab is in the background. Traders click "Allow" once and receive popups instantly.</li>
              <li><strong>Telegram</strong> — each trader links their Telegram account and receives formatted alerts on their phone even when away from the platform.</li>
            </ul>
            <p><strong>Events that trigger notifications:</strong> Circuit breaker triggered, Stop-loss hit, Re-entry placed, End-of-day square-off at 3:20 PM, VPS trading engine offline (admin only).</p>
            <p><strong>Recipients:</strong> Each trader receives their own notifications. Admins receive all alerts.</p>
          </>}
        />
        <FaqItem
          q="What is the event day calendar and how does it work?"
          tag="Event Calendar · A7"
          a={<>
            <p>A separate calendar page where the admin can mark dates as <strong>"no trade" days</strong>:</p>
            <ul>
              <li>Exchange holidays</li>
              <li>Event days (RBI policy, Budget day, election results, etc.)</li>
              <li>Any custom date the admin wants to block</li>
            </ul>
            <p>On marked dates, the system will not auto-initiate any trades for any trader. However, individual traders retain the ability to manually start trading — this is not system-controllable.</p>
          </>}
        />
      </FaqSection>

      {/* Summary counts */}
      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">📊</span><h2>Q&A Status Summary</h2></div>
        <div className="kb-summary-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <SummaryCard label="Common / Cross-Strategy" value="8 / 8" sub="All answered" icon="✅" />
          <SummaryCard label="RSI Straddle" value="5 / 5" sub="All answered" icon="✅" />
          <SummaryCard label="Trending Expiry" value="7 / 7" sub="All answered" icon="✅" />
          <SummaryCard label="Order Execution" value="6 / 6" sub="All answered" icon="✅" />
          <SummaryCard label="Risk & Margin" value="4 / 4" sub="All answered" icon="✅" />
          <SummaryCard label="System Architecture" value="2 / 4" sub="F2, F4 pending" icon="⏳" />
          <SummaryCard label="Total Answered" value="32 / 34" sub="94% complete" icon="🎯" />
          <SummaryCard label="Last Updated" value="Jun 2026" sub="Client confirmed" icon="📅" />
        </div>
      </section>
    </>
  )
}

/* ── Styles ── */
const kbStyles = `
  .kb-page { display: flex; gap: 1.25rem; align-items: flex-start; }

  /* Sidebar */
  .kb-sidebar {
    position: sticky; top: 80px; width: 260px; flex-shrink: 0;
    background: var(--bg-card); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 0.75rem;
    max-height: calc(100vh - 100px); overflow-y: auto;
  }
  .kb-sidebar-title {
    font-size: 0.8125rem; font-weight: 800;
    background: linear-gradient(135deg, var(--accent), var(--purple));
    -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
    padding: 0.25rem 0.375rem; line-height: 1.3;
  }
  .kb-sidebar-sub {
    font-size: 0.5625rem; font-weight: 600; color: var(--text-muted);
    text-transform: uppercase; letter-spacing: 0.08em;
    padding: 0.125rem 0.375rem;
  }
  .kb-sidebar-divider { height: 1px; background: var(--border); margin: 0.5rem 0; }
  .kb-sidebar-footer {
    font-size: 0.5rem; color: var(--text-muted); padding: 0.25rem 0.375rem;
    text-align: center; letter-spacing: 0.04em;
  }

  .kb-content { flex: 1; min-width: 0; }

  /* Tabs (vertical in sidebar) */
  .kb-tab {
    display: flex; align-items: center; gap: 0.375rem; width: 100%;
    padding: 0.4rem 0.5rem; background: none; border: none; border-radius: var(--radius-sm);
    color: var(--text-secondary); font-size: 0.6875rem; font-weight: 500;
    cursor: pointer; transition: all 0.12s; text-align: left; white-space: nowrap;
  }
  .kb-tab.pending { opacity: 0.45; }
  .kb-tab:hover { background: rgba(255,255,255,0.04); color: var(--text-primary); }
  .kb-tab.active { background: var(--accent-glow); color: var(--accent); font-weight: 700; }
  .kb-tab.active::before {
    content: ''; position: absolute; right: 0; top: 25%; bottom: 25%;
    width: 2px; background: var(--accent); border-radius: 2px;
  }
  .kb-tab { position: relative; }
  .kb-tab-num {
    font-size: 0.5rem; font-weight: 800; background: rgba(56,189,248,0.12);
    padding: 0.05rem 0.3rem; border-radius: 3px; font-family: 'JetBrains Mono', monospace;
    color: var(--accent); flex-shrink: 0;
  }
  .kb-tab.active .kb-tab-num { background: rgba(56,189,248,0.25); }
  .kb-tab-name { flex: 1; font-weight: inherit; overflow: hidden; text-overflow: ellipsis; }
  .kb-tab-badge {
    font-size: 0.4375rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    padding: 0.05rem 0.3rem; border-radius: 999px; flex-shrink: 0;
  }
  .kb-tab-badge.green { background: rgba(74,222,128,0.1); color: var(--green); }
  .kb-tab-badge.purple { background: rgba(167,139,250,0.1); color: var(--purple); }
  .kb-tab-badge.muted { background: rgba(255,255,255,0.05); color: var(--text-muted); }
  .kb-tab-badge.gold { background: rgba(234,179,8,0.12); color: var(--yellow, #eab308); }

  /* FAQ */
  .faq-item {
    border-bottom: 1px solid var(--border); padding: 0.75rem 1.5rem;
    cursor: pointer; transition: background 0.12s;
  }
  .faq-item:last-child { border-bottom: none; }
  .faq-item:hover { background: rgba(255,255,255,0.02); }
  .faq-q {
    display: flex; align-items: center; gap: 0.625rem;
    font-size: 0.8125rem; font-weight: 600; color: var(--text-primary); line-height: 1.5;
    user-select: none;
  }
  .faq-icon { color: var(--accent); font-size: 0.75rem; flex-shrink: 0; width: 12px; }
  .faq-tag {
    margin-left: auto; flex-shrink: 0;
    font-size: 0.4375rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
    background: rgba(56,189,248,0.08); color: var(--accent);
    border: 1px solid rgba(56,189,248,0.2); border-radius: 999px;
    padding: 0.1rem 0.4rem;
  }
  .faq-a {
    margin-top: 0.625rem; padding: 0.75rem 1rem;
    background: rgba(56,189,248,0.03); border-left: 3px solid var(--accent);
    border-radius: 0 6px 6px 0;
    font-size: 0.8125rem; line-height: 1.75; color: var(--text-secondary);
  }
  .faq-a p { margin: 0.25rem 0; }
  .faq-a ul { margin: 0.375rem 0; padding-left: 1.25rem; }
  .faq-a li { margin-bottom: 0.25rem; }
  .faq-a strong { color: var(--text-primary); }

  /* Hero */
  .kb-hero {
    position: relative; padding: 2rem 2rem 1.75rem; margin-bottom: 1rem;
    background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius);
    overflow: hidden;
  }
  .kb-hero-glow {
    position: absolute; top: -60px; right: -40px; width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(56,189,248,0.08) 0%, transparent 70%);
    pointer-events: none;
  }
  .kb-hero-content { position: relative; z-index: 1; }
  .kb-hero-title {
    font-size: 1.75rem; font-weight: 900; margin: 0.75rem 0 0.25rem;
    background: linear-gradient(135deg, var(--accent), var(--purple));
    -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  }
  .kb-hero-sub { color: var(--text-muted); font-size: 0.9375rem; font-weight: 500; margin: 0; }
  .kb-badge-row { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .kb-badge {
    display: inline-flex; padding: 0.15rem 0.625rem; border-radius: 999px;
    font-size: 0.5625rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
  }
  .kb-badge.blue { background: rgba(56,189,248,0.1); color: var(--accent); border: 1px solid rgba(56,189,248,0.2); }
  .kb-badge.green { background: rgba(74,222,128,0.1); color: var(--green); border: 1px solid rgba(74,222,128,0.2); }
  .kb-badge.purple { background: rgba(167,139,250,0.1); color: var(--purple); border: 1px solid rgba(167,139,250,0.2); }
  .kb-badge.muted { background: rgba(255,255,255,0.04); color: var(--text-muted); border: 1px solid rgba(255,255,255,0.1); }

  /* Sections */
  .kb-sec {
    background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius);
    margin-bottom: 0.75rem; overflow: hidden;
  }
  .kb-sec.suggestions { border-color: rgba(234,179,8,0.2); }
  .kb-sec-head {
    display: flex; align-items: center; gap: 0.625rem;
    padding: 0.875rem 1.5rem; border-bottom: 1px solid var(--border);
  }
  .kb-sec-icon { font-size: 1.125rem; }
  .kb-sec-head h2 {
    font-size: 1rem; font-weight: 700; margin: 0; color: var(--text-primary);
    display: flex; align-items: center; gap: 0.375rem;
  }
  .kb-prose { padding: 1.25rem 1.5rem; }
  .kb-prose p { font-size: 0.8125rem; line-height: 1.75; color: var(--text-secondary); margin-bottom: 0.5rem; }
  .kb-prose strong { color: var(--text-primary); }

  /* Meta Chips */
  .kb-meta-row { display: flex; gap: 0.75rem; flex-wrap: wrap; margin-top: 0.75rem; }
  .kb-meta-chip {
    display: flex; flex-direction: column; gap: 0.125rem;
    padding: 0.5rem 1rem; background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; flex: 1; min-width: 200px;
  }
  .kb-meta-k { font-size: 0.5rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--accent); }
  .kb-meta-v { font-size: 0.8125rem; font-weight: 600; color: var(--text-primary); }

  /* Rules */
  .kb-rule { display: flex; gap: 0.875rem; margin-bottom: 1rem; }
  .kb-rule:last-child { margin-bottom: 0; }
  .kb-rule-num {
    width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, rgba(56,189,248,0.15), rgba(167,139,250,0.1));
    border: 1px solid rgba(56,189,248,0.2);
    font-size: 0.6875rem; font-weight: 800; color: var(--accent);
    font-family: 'JetBrains Mono', monospace; margin-top: 0.125rem;
  }
  .kb-rule-body { font-size: 0.8125rem; line-height: 1.75; color: var(--text-secondary); flex: 1; }
  .kb-rule-body strong { color: var(--text-primary); }

  /* Example */
  .kb-example {
    margin-top: 0.625rem; padding: 0.625rem 0.875rem;
    background: rgba(56,189,248,0.04); border-left: 3px solid var(--accent);
    border-radius: 0 6px 6px 0; font-size: 0.75rem; line-height: 1.65; color: var(--text-secondary);
  }
  .kb-example strong { color: var(--accent); font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.04em; }

  /* Formula Row */
  .kb-formula-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 0.75rem; margin-top: 1.25rem; }
  .kb-formula-card {
    padding: 0.875rem; border-radius: 8px; text-align: center;
    background: linear-gradient(135deg, rgba(74,222,128,0.04), rgba(74,222,128,0.01));
    border: 1px solid rgba(74,222,128,0.15);
  }
  .kb-formula-card.warn {
    background: linear-gradient(135deg, rgba(234,179,8,0.04), rgba(234,179,8,0.01));
    border-color: rgba(234,179,8,0.15);
  }
  .kb-formula-label { font-size: 0.5625rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--green); margin-bottom: 0.25rem; }
  .kb-formula-card.warn .kb-formula-label { color: var(--yellow); }
  .kb-formula-val { font-size: 0.9375rem; font-weight: 800; color: var(--text-primary); font-family: 'JetBrains Mono', monospace; }
  .kb-formula-sub { font-size: 0.625rem; color: var(--text-muted); margin-top: 0.125rem; }

  /* Decision Cards */
  .kb-decision-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-top: 0.75rem; }
  .kb-decision-card { padding: 0.875rem; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-card); }
  .kb-decision-card.green { border-color: rgba(74,222,128,0.25); background: rgba(74,222,128,0.03); }
  .kb-decision-card.red { border-color: rgba(248,113,113,0.25); background: rgba(248,113,113,0.03); }
  .kb-decision-header { font-size: 0.8125rem; font-weight: 800; margin-bottom: 0.375rem; font-family: 'JetBrains Mono', monospace; }
  .kb-decision-card.green .kb-decision-header { color: var(--green); }
  .kb-decision-card.red .kb-decision-header { color: var(--red); }
  .kb-decision-body { font-size: 0.75rem; line-height: 1.65; color: var(--text-secondary); }
  .kb-decision-body strong { color: var(--text-primary); }

  /* Flow */
  .kb-flow {
    display: flex; align-items: center; gap: 0.375rem; flex-wrap: wrap;
    padding: 0.75rem; margin-top: 1rem;
    background: rgba(255,255,255,0.02); border: 1px solid var(--border); border-radius: 8px;
  }
  .kb-flow-step {
    padding: 0.3rem 0.625rem; border: 1px solid var(--border); border-radius: 6px;
    font-size: 0.6875rem; font-weight: 600; color: var(--text-primary); background: var(--bg-card); white-space: nowrap;
  }
  .kb-flow-step.exit { border-color: var(--red); color: var(--red); }
  .kb-flow-step.green { border-color: var(--green); color: var(--green); }
  .kb-flow-step.accent { border-color: var(--accent); color: var(--accent); }
  .kb-flow-arrow { color: var(--text-muted); font-size: 0.75rem; }

  /* Summary Grid */
  .kb-summary-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0.5rem; padding: 1rem 1.5rem; }
  .kb-summary-card {
    padding: 0.75rem 0.5rem; border-radius: 8px; text-align: center;
    background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); transition: all 0.2s;
  }
  .kb-summary-card:hover { border-color: var(--accent); background: rgba(56,189,248,0.03); transform: translateY(-1px); }
  .kb-summary-icon { font-size: 1.125rem; margin-bottom: 0.125rem; }
  .kb-summary-label { font-size: 0.4375rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-muted); margin-bottom: 0.125rem; }
  .kb-summary-value { font-size: 0.8125rem; font-weight: 800; color: var(--text-primary); font-family: 'JetBrains Mono', monospace; }
  .kb-summary-sub { font-size: 0.5625rem; color: var(--text-muted); margin-top: 0.125rem; line-height: 1.3; }

  /* Disclaimer */
  .kb-disclaimer {
    text-align: center; padding: 0.875rem 1.5rem; margin-bottom: 0.75rem;
    font-size: 0.6875rem; font-style: italic; color: var(--text-muted);
    border: 1px dashed var(--border); border-radius: var(--radius); background: rgba(255,255,255,0.01);
  }

  /* Suggestions */
  .kb-suggestion-note {
    padding: 0.625rem 1.5rem; font-size: 0.75rem; color: var(--text-muted);
    background: rgba(234,179,8,0.04); border-bottom: 1px solid rgba(234,179,8,0.1); line-height: 1.5;
  }
  .kb-suggestion-note strong { color: var(--yellow); }
  .kb-suggestion { display: flex; gap: 0.75rem; margin-bottom: 1rem; }
  .kb-suggestion:last-child { margin-bottom: 0; }
  .kb-suggestion-num {
    width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(234,179,8,0.1); border: 1px solid rgba(234,179,8,0.2);
    font-size: 0.625rem; font-weight: 800; color: var(--yellow);
    font-family: 'JetBrains Mono', monospace; margin-top: 0.125rem;
  }
  .kb-suggestion strong { display: block; font-size: 0.8125rem; color: var(--text-primary); margin-bottom: 0.125rem; }
  .kb-suggestion p { font-size: 0.75rem; line-height: 1.65; color: var(--text-secondary); margin: 0; }

  /* Placeholder */
  .kb-placeholder {
    text-align: center; padding: 2rem; margin-top: 0.75rem;
    background: rgba(255,255,255,0.02); border: 1px dashed var(--border); border-radius: 8px;
  }
  .kb-placeholder-icon { font-size: 2rem; display: block; margin-bottom: 0.5rem; }
  .kb-placeholder p { color: var(--text-muted); margin: 0.25rem 0; }
  .kb-placeholder strong { color: var(--text-secondary); }

  /* Footer */
  .kb-footer {
    text-align: center; padding: 1.25rem; color: var(--text-muted);
    font-size: 0.6875rem; border-top: 1px solid var(--border); margin-top: 0.5rem;
  }
  .kb-footer p { margin: 0.125rem 0; }

  @media (max-width: 800px) {
    .kb-page { flex-direction: column; }
    .kb-sidebar {
      position: static; width: 100%; max-height: none;
      display: flex; align-items: center; gap: 0.375rem;
      overflow-x: auto; padding: 0.375rem;
    }
    .kb-sidebar-title { font-size: 0.6875rem; white-space: nowrap; flex-shrink: 0; }
    .kb-sidebar-sub { display: none; }
    .kb-sidebar-divider { width: 1px; height: 20px; margin: 0 0.25rem; flex-shrink: 0; }
    .kb-sidebar-footer { display: none; }
    .kb-tab { width: auto; flex-shrink: 0; font-size: 0.625rem; padding: 0.35rem 0.5rem; }
    .kb-formula-row, .kb-decision-grid { grid-template-columns: 1fr; }
    .kb-summary-grid { grid-template-columns: repeat(2, 1fr); }
    .kb-hero-title { font-size: 1.375rem; }
  }
`

/* ═══════════════════════════════════════════════════════════════════
   SYSTEM ARCHITECTURE
   ═══════════════════════════════════════════════════════════════════ */

function SystemArchitectureContent() {
  /* ── Animated SVG flow diagram ── */
  const W = 920, H = 780
  const vb = '0 0 ' + W + ' ' + H
  const svgCss = '.flow-line { stroke-dasharray: 6 4; animation: flowDash 1.2s linear infinite; } @keyframes flowDash { to { stroke-dashoffset: -20; } } .glow-green { filter: drop-shadow(0 0 4px rgba(74,222,128,0.5)); } .glow-yellow { filter: drop-shadow(0 0 4px rgba(251,191,36,0.5)); } .pulse { animation: pulse 2s ease-in-out infinite; } @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }'
  const flowDiagram = (
    <svg viewBox={vb} width="100%" style={{ maxWidth: W, display: 'block', margin: '1.5rem auto 0' }}>
      <defs>
        {/* Arrow marker */}
        <marker id="ah" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill="#64748b" /></marker>
        <marker id="ahG" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill="#4ade80" /></marker>
        <marker id="ahY" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill="#fbbf24" /></marker>
        <marker id="ahP" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill="#a78bfa" /></marker>
        {/* Animated dash */}
        <style>{svgCss}</style>
      </defs>

      {/* ══════ LAYER 1: Browser ══════ */}
      <rect x="20" y="10" width={W-40} height="80" rx="14" fill="rgba(6,182,212,0.08)" stroke="rgba(6,182,212,0.35)" strokeWidth="1.5" />
      <text x="36" y="30" fill="rgba(6,182,212,0.5)" fontSize="9" fontWeight="700" letterSpacing="0.1em">LAYER 1 · CLIENT</text>
      {/* Browser box */}
      <rect x={W/2-120} y="36" width="240" height="44" rx="10" fill="rgba(6,182,212,0.15)" stroke="rgba(6,182,212,0.5)" strokeWidth="1.5" />
      <text x={W/2} y="55" textAnchor="middle" fill="#e0f2fe" fontSize="13" fontWeight="700">🌐  Trader / Admin Browser</text>
      <text x={W/2} y="71" textAnchor="middle" fill="#67e8f9" fontSize="9" fontWeight="600">React 18 + TypeScript + Vite · la-money.co.in</text>

      {/* ── Arrow: Browser → Cloudflare ── */}
      <line x1={W/2-80} y1="90" x2={W/2-80} y2="120" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#ah)" className="flow-line" />
      <text x={W/2-74} y="110" fill="#64748b" fontSize="8" fontWeight="600">HTTPS</text>
      <line x1={W/2} y1="90" x2={W/2} y2="120" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#ah)" className="flow-line" />
      <text x={W/2+6} y="110" fill="#64748b" fontSize="8" fontWeight="600">REST</text>
      <line x1={W/2+80} y1="90" x2={W/2+80} y2="120" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#ah)" className="flow-line" />
      <text x={W/2+86} y="110" fill="#64748b" fontSize="8" fontWeight="600">SSE</text>

      {/* ══════ LAYER 2: Cloudflare ══════ */}
      <rect x="20" y="120" width={W-40} height="200" rx="14" fill="rgba(139,92,246,0.06)" stroke="rgba(139,92,246,0.3)" strokeWidth="1.5" />
      <text x="36" y="140" fill="rgba(139,92,246,0.5)" fontSize="9" fontWeight="700" letterSpacing="0.1em">☁️ LAYER 2 · CLOUDFLARE EDGE NETWORK</text>

      {/* Pages */}
      <rect x="40" y="152" width="150" height="60" rx="8" fill="rgba(139,92,246,0.12)" stroke="rgba(139,92,246,0.45)" strokeWidth="1" />
      <text x="115" y="172" textAnchor="middle" fill="#c4b5fd" fontSize="11" fontWeight="700">📄 Cloudflare Pages</text>
      <text x="115" y="186" textAnchor="middle" fill="#94a3b8" fontSize="9">Frontend SPA</text>
      <text x="115" y="199" textAnchor="middle" fill="#a78bfa" fontSize="8" fontWeight="600">CDN · Global Edge</text>

      {/* Worker */}
      <rect x="210" y="152" width="290" height="60" rx="8" fill="rgba(139,92,246,0.18)" stroke="rgba(139,92,246,0.55)" strokeWidth="1.5" />
      <text x="355" y="170" textAnchor="middle" fill="#e2e8f0" fontSize="12" fontWeight="700">⚡ CF Worker — lamoney-api</text>
      <text x="355" y="184" textAnchor="middle" fill="#94a3b8" fontSize="9">JWT Auth · CORS · Rate Limiting · Hono Router</text>
      <text x="355" y="199" textAnchor="middle" fill="#a78bfa" fontSize="8" fontWeight="600">/auth  /brokers  /executions  /trades  /livewatch</text>

      {/* D1 Database (cylinder shape) */}
      <ellipse cx="580" cy="160" rx="50" ry="8" fill="rgba(245,158,11,0.15)" stroke="rgba(245,158,11,0.45)" strokeWidth="1" />
      <rect x="530" y="160" width="100" height="30" fill="rgba(245,158,11,0.1)" stroke="none" />
      <ellipse cx="580" cy="190" rx="50" ry="8" fill="rgba(245,158,11,0.15)" stroke="rgba(245,158,11,0.45)" strokeWidth="1" />
      <line x1="530" y1="160" x2="530" y2="190" stroke="rgba(245,158,11,0.45)" strokeWidth="1" />
      <line x1="630" y1="160" x2="630" y2="190" stroke="rgba(245,158,11,0.45)" strokeWidth="1" />
      <text x="580" y="178" textAnchor="middle" fill="#fbbf24" fontSize="10" fontWeight="700">🗄️ D1 SQLite</text>

      {/* KV Store */}
      <rect x="660" y="152" width="115" height="60" rx="8" fill="rgba(245,158,11,0.1)" stroke="rgba(245,158,11,0.35)" strokeWidth="1" />
      <text x="717" y="170" textAnchor="middle" fill="#fbbf24" fontSize="10" fontWeight="700">⚡ KV Store</text>
      <text x="717" y="184" textAnchor="middle" fill="#94a3b8" fontSize="9">Positions · Prices</text>
      <text x="717" y="198" textAnchor="middle" fill="#fb923c" fontSize="8" fontWeight="600">Sub-ms reads</text>

      {/* Durable Objects */}
      <rect x="800" y="152" width="90" height="60" rx="8" fill="rgba(168,85,247,0.12)" stroke="rgba(168,85,247,0.4)" strokeWidth="1" />
      <text x="845" y="170" textAnchor="middle" fill="#c084fc" fontSize="10" fontWeight="700">🔮 DOs</text>
      <text x="845" y="184" textAnchor="middle" fill="#94a3b8" fontSize="8">CircuitBreaker</text>
      <text x="845" y="196" textAnchor="middle" fill="#94a3b8" fontSize="8">MarketDataHub</text>

      {/* Worker → D1 arrow */}
      <line x1="500" y1="175" x2="528" y2="175" stroke="#fb923c" strokeWidth="1" markerEnd="url(#ah)" className="flow-line" />
      {/* Worker → KV arrow */}
      <line x1="500" y1="182" x2="528" y2="182" stroke="transparent" />

      {/* CF Tunnel box */}
      <rect x="300" y="228" width="300" height="36" rx="8" fill="rgba(249,115,22,0.1)" stroke="rgba(249,115,22,0.4)" strokeWidth="1.5" />
      <text x="450" y="250" textAnchor="middle" fill="#fb923c" fontSize="11" fontWeight="700">🔒 Cloudflare Tunnel — Zero-Trust mTLS</text>
      <text x="450" y="260" textAnchor="middle" fill="#94a3b8" fontSize="8">Encrypted tunnel · No public ports exposed</text>

      {/* Worker → Tunnel arrow */}
      <line x1="355" y1="212" x2="355" y2="228" stroke="#a78bfa" strokeWidth="1.5" markerEnd="url(#ahP)" className="flow-line" />

      {/* ── Arrow: CF → VPS ── */}
      <line x1="450" y1="264" x2="450" y2="320" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#ah)" className="flow-line" />
      <rect x="416" y="280" width="68" height="16" rx="4" fill="rgba(15,23,42,0.9)" stroke="rgba(100,116,139,0.25)" strokeWidth="1" />
      <text x="450" y="292" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="600">HTTPS ↕</text>

      {/* ══════ LAYER 3: VPS ══════ */}
      <rect x="20" y="330" width={W-40} height="150" rx="14" fill="rgba(34,197,94,0.06)" stroke="rgba(34,197,94,0.3)" strokeWidth="1.5" />
      <text x="36" y="350" fill="rgba(34,197,94,0.5)" fontSize="9" fontWeight="700" letterSpacing="0.1em">🖥️ LAYER 3 · HOSTINGER VPS — 80.225.242.244 (UBUNTU · DOCKER)</text>

      {/* Trading Engine */}
      <rect x="40" y="362" width="430" height="100" rx="10" fill="rgba(34,197,94,0.1)" stroke="rgba(34,197,94,0.4)" strokeWidth="1.5" />
      <text x="60" y="382" fill="#4ade80" fontSize="12" fontWeight="700">🐳 lamoney-trading-engine</text>
      <text x="60" y="398" fill="#94a3b8" fontSize="10">Node.js 20 · server.js · 127.0.0.1:3000</text>
      {/* Sub-modules inside */}
      <rect x="55" y="410" width="96" height="22" rx="5" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.3)" strokeWidth="1" />
      <text x="103" y="424" textAnchor="middle" fill="#86efac" fontSize="8" fontWeight="600">Broker Mgmt</text>
      <rect x="160" y="410" width="96" height="22" rx="5" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.3)" strokeWidth="1" />
      <text x="208" y="424" textAnchor="middle" fill="#86efac" fontSize="8" fontWeight="600">Order Routing</text>
      <rect x="265" y="410" width="96" height="22" rx="5" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.3)" strokeWidth="1" />
      <text x="313" y="424" textAnchor="middle" fill="#86efac" fontSize="8" fontWeight="600">Market Data</text>
      <rect x="55" y="438" width="96" height="22" rx="5" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.3)" strokeWidth="1" />
      <text x="103" y="452" textAnchor="middle" fill="#86efac" fontSize="8" fontWeight="600">SSE Events</text>
      <rect x="160" y="438" width="96" height="22" rx="5" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.3)" strokeWidth="1" />
      <text x="208" y="452" textAnchor="middle" fill="#86efac" fontSize="8" fontWeight="600">Contracts Cache</text>
      <rect x="265" y="438" width="96" height="22" rx="5" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.3)" strokeWidth="1" />
      <text x="313" y="452" textAnchor="middle" fill="#86efac" fontSize="8" fontWeight="600">Risk Poller</text>
      <rect x="370" y="410" width="86" height="50" rx="5" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.3)" strokeWidth="1" />
      <text x="413" y="430" textAnchor="middle" fill="#86efac" fontSize="8" fontWeight="600">Strategy</text>
      <text x="413" y="444" textAnchor="middle" fill="#86efac" fontSize="8" fontWeight="600">RSI / Trending</text>

      {/* Tunnel Container */}
      <rect x="510" y="362" width="185" height="100" rx="10" fill="rgba(249,115,22,0.08)" stroke="rgba(249,115,22,0.35)" strokeWidth="1" />
      <text x="602" y="382" textAnchor="middle" fill="#fb923c" fontSize="11" fontWeight="700">🌉 lamoney-tunnel</text>
      <text x="602" y="398" textAnchor="middle" fill="#94a3b8" fontSize="9">cloudflared Docker</text>
      <text x="602" y="414" textAnchor="middle" fill="#94a3b8" fontSize="9">network_mode: host</text>
      <text x="602" y="434" textAnchor="middle" fill="#fb923c" fontSize="9" fontWeight="600">:3000 → CF Edge</text>
      <rect x="548" y="444" width="56" height="14" rx="3" fill="rgba(249,115,22,0.15)" stroke="rgba(249,115,22,0.3)" strokeWidth="1" />
      <text x="576" y="454" textAnchor="middle" fill="#fb923c" fontSize="7" fontWeight="600">Zero Trust</text>
      <rect x="610" y="444" width="68" height="14" rx="3" fill="rgba(249,115,22,0.15)" stroke="rgba(249,115,22,0.3)" strokeWidth="1" />
      <text x="644" y="454" textAnchor="middle" fill="#fb923c" fontSize="7" fontWeight="600">Auto-restart</text>

      {/* Engine → Tunnel internal arrow */}
      <line x1="470" y1="412" x2="508" y2="412" stroke="rgba(148,163,184,0.4)" strokeWidth="1" markerEnd="url(#ah)" strokeDasharray="4 3" />

      {/* ── Arrow: VPS → Greeksoft ── */}
      <line x1="160" y1="480" x2="160" y2="540" stroke="#4ade80" strokeWidth="1.5" markerEnd="url(#ahG)" className="flow-line" />
      <rect x="110" y="498" width="100" height="16" rx="4" fill="rgba(15,23,42,0.9)" stroke="rgba(100,116,139,0.25)" strokeWidth="1" />
      <text x="160" y="510" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="600">REST / JSON</text>

      <line x1="400" y1="480" x2="400" y2="540" stroke="#4ade80" strokeWidth="1.5" markerEnd="url(#ahG)" className="flow-line" />
      <rect x="344" y="498" width="112" height="16" rx="4" fill="rgba(15,23,42,0.9)" stroke="rgba(100,116,139,0.25)" strokeWidth="1" />
      <text x="400" y="510" textAnchor="middle" fill="#4ade80" fontSize="8" fontWeight="600">WS Raw JSON ✅</text>

      <line x1="650" y1="480" x2="650" y2="540" stroke="#fbbf24" strokeWidth="1.5" markerEnd="url(#ahY)" className="flow-line" />
      <rect x="594" y="498" width="112" height="16" rx="4" fill="rgba(15,23,42,0.9)" stroke="rgba(245,158,11,0.25)" strokeWidth="1" />
      <text x="650" y="510" textAnchor="middle" fill="#fbbf24" fontSize="8" fontWeight="600">WS jloginNew ⚠️</text>

      {/* ══════ LAYER 4: Greeksoft ══════ */}
      <rect x="20" y="550" width={W-40} height="220" rx="14" fill="rgba(239,68,68,0.05)" stroke="rgba(239,68,68,0.25)" strokeWidth="1.5" />
      <text x="36" y="570" fill="rgba(239,68,68,0.5)" fontSize="9" fontWeight="700" letterSpacing="0.1em">📡 LAYER 4 · GREEKSOFT SVIS SERVERS</text>

      {/* Session Token server */}
      <rect x="40" y="582" width="190" height="80" rx="10" fill="rgba(239,68,68,0.1)" stroke="rgba(239,68,68,0.35)" strokeWidth="1" />
      <text x="135" y="602" textAnchor="middle" fill="#fca5a5" fontSize="11" fontWeight="700">🔑 Session Token</text>
      <text x="135" y="618" textAnchor="middle" fill="#94a3b8" fontSize="9">greekapi.greeksoft.in:3001</text>
      <text x="135" y="634" textAnchor="middle" fill="#94a3b8" fontSize="9">JWT Auth · getSessionToken</text>
      <circle cx="58" cy="650" r="4" fill="#4ade80" className="glow-green" />
      <text x="68" y="654" fill="#4ade80" fontSize="9" fontWeight="600">Active</text>

      {/* REST API server */}
      <rect x="250" y="582" width="190" height="80" rx="10" fill="rgba(239,68,68,0.1)" stroke="rgba(239,68,68,0.35)" strokeWidth="1" />
      <text x="345" y="602" textAnchor="middle" fill="#fca5a5" fontSize="11" fontWeight="700">🌐 REST API (Arachne)</text>
      <text x="345" y="618" textAnchor="middle" fill="#94a3b8" fontSize="9">vpn.greeksoft.in:8230</text>
      <text x="345" y="634" textAnchor="middle" fill="#94a3b8" fontSize="9">getSessionId · jloginNew · Orders</text>
      <circle cx="268" cy="650" r="4" fill="#4ade80" className="glow-green" />
      <text x="278" y="654" fill="#4ade80" fontSize="9" fontWeight="600">Active</text>

      {/* IRIS WS */}
      <rect x="460" y="582" width="190" height="80" rx="10" fill="rgba(34,197,94,0.1)" stroke="rgba(34,197,94,0.5)" strokeWidth="2" className="glow-green" />
      <text x="555" y="602" textAnchor="middle" fill="#4ade80" fontSize="11" fontWeight="700">⚡ IRIS WebSocket</text>
      <text x="555" y="618" textAnchor="middle" fill="#94a3b8" fontSize="9">vpn.greeksoft.in:8228</text>
      <text x="555" y="634" textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600">Orders · getSessionId session</text>
      <circle cx="478" cy="650" r="4" fill="#4ade80" className="glow-green" />
      <text x="488" y="654" fill="#4ade80" fontSize="9" fontWeight="700">✅ Connected</text>

      {/* Apollo WS */}
      <rect x="670" y="582" width="195" height="80" rx="10" fill="rgba(245,158,11,0.1)" stroke="rgba(245,158,11,0.5)" strokeWidth="2" className="glow-yellow" />
      <text x="768" y="602" textAnchor="middle" fill="#fbbf24" fontSize="11" fontWeight="700">📊 Apollo WebSocket</text>
      <text x="768" y="618" textAnchor="middle" fill="#94a3b8" fontSize="9">vpn.greeksoft.in:8229</text>
      <text x="768" y="634" textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600">Market Data · jloginNew session</text>
      <circle cx="688" cy="650" r="4" fill="#fbbf24" className="glow-yellow pulse" />
      <text x="698" y="654" fill="#fbbf24" fontSize="9" fontWeight="700">⚠️ Pending Password</text>

      {/* ── Session Flow (bottom) ── */}
      <text x="36" y="690" fill="rgba(99,102,241,0.6)" fontSize="9" fontWeight="700" letterSpacing="0.1em">SESSION FLOW</text>
      {/* Flow chain */}
      {[
        { x: 40, w: 120, label: '1. getSessionToken', sub: 'JWT', color: '#818cf8', ok: true },
        { x: 172, w: 100, label: '2. getFlagValues', sub: 'Config', color: '#818cf8', ok: true },
        { x: 284, w: 120, label: '3. getSessionId', sub: 'IRIS Session', color: '#4ade80', ok: true },
        { x: 416, w: 100, label: '4. IRIS Login', sub: 'Raw JSON WS', color: '#4ade80', ok: true },
        { x: 528, w: 100, label: '5. jloginNew', sub: 'Apollo Session', color: '#fbbf24', ok: false },
        { x: 640, w: 110, label: '6. Apollo Login', sub: 'Market Data WS', color: '#fbbf24', ok: false },
      ].map((s, i) => (
        <g key={i}>
          <rect x={s.x} y="700" width={s.w} height="42" rx="6" fill={`${s.color}15`} stroke={`${s.color}50`} strokeWidth="1" />
          <text x={s.x + s.w/2} y="716" textAnchor="middle" fill={s.color} fontSize="8" fontWeight="700">{s.label}</text>
          <text x={s.x + s.w/2} y="730" textAnchor="middle" fill="#94a3b8" fontSize="8">{s.sub}</text>
          <circle cx={s.x + s.w - 8} cy="706" r="3" fill={s.ok ? '#4ade80' : '#fbbf24'} className={s.ok ? 'glow-green' : 'glow-yellow'} />
          {i < 5 && <line x1={s.x + s.w} y1="721" x2={s.x + s.w + 12} y2="721" stroke={s.color} strokeWidth="1" markerEnd="url(#ah)" className="flow-line" />}
        </g>
      ))}

      {/* Legend */}
      <rect x="770" y="698" width="110" height="46" rx="6" fill="rgba(15,23,42,0.7)" stroke="rgba(100,116,139,0.2)" strokeWidth="1" />
      <circle cx="782" cy="712" r="4" fill="#4ade80" /><text x="792" y="716" fill="#94a3b8" fontSize="8">Active</text>
      <circle cx="832" cy="712" r="4" fill="#fbbf24" /><text x="842" y="716" fill="#94a3b8" fontSize="8">Pending</text>
      <line x1="780" y1="730" x2="800" y2="730" stroke="#64748b" strokeWidth="1.5" strokeDasharray="6 4" />
      <text x="808" y="734" fill="#94a3b8" fontSize="8">Data Flow</text>
    </svg>
  )

  return (
    <>
      <div className="kb-hero">
        <div className="kb-hero-glow" style={{ background: 'radial-gradient(circle at center, rgba(245,158,11,0.15) 0%, transparent 60%)' }} />
        <div className="kb-hero-content">
          <div className="kb-badge-row">
            <span className="kb-badge orange">Core System</span>
            <span className="kb-badge purple">Cloudflare</span>
            <span className="kb-badge green">VPS · Hostinger</span>
            <span className="kb-badge blue">Greeksoft SVIS</span>
          </div>
          <h2 className="kb-hero-title">System Architecture</h2>
          <p className="kb-hero-sub">Infrastructure · Data Flow · Connection Status</p>
        </div>
      </div>

      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">🗺️</span><h2>Live Architecture Diagram</h2></div>
        <div className="kb-prose" style={{ overflowX: 'auto' }}>
          {flowDiagram}
        </div>
      </section>

      <section className="kb-sec">
        <div className="kb-sec-head"><span className="kb-sec-icon">⚙️</span><h2>Functional Modules</h2></div>
        <div className="kb-prose">

          <div className="kb-rule" style={{ marginTop: '1.5rem' }}>
            <span className="kb-rule-num" style={{ background: '#0284c7', color: 'white' }}>1</span>
            <div className="kb-rule-body">
              <strong>The Cloudflare Worker API (Edge Gateway)</strong><br />
              Acts as the central nervous system. All HTTP requests from the React frontend pass through here. It handles JWT authentication, CORS, rate-limiting, and queries the D1 SQLite database. It hosts <strong>Durable Objects</strong> (single-instance global state coordinators) for Market Data and the Global Circuit Breaker.
            </div>
          </div>

          <div className="kb-rule">
            <span className="kb-rule-num" style={{ background: '#16a34a', color: 'white' }}>2</span>
            <div className="kb-rule-body">
              <strong>The VPS Trading Engine (Node.js)</strong><br />
              Runs 24/7 on a Hostinger VPS (80.225.242.244) inside Docker. It maintains persistent WebSocket connections to Greeksoft. IRIS uses <code>getSessionId</code> for per-client order streams. Apollo uses <code>jloginNew</code> for shared market data. The engine creates a <strong>Shared Apollo multiplexer</strong>—receiving all indices data on one socket and distributing it to all running strategies internally.
            </div>
          </div>

          <div className="kb-rule">
            <span className="kb-rule-num" style={{ background: '#9333ea', color: 'white' }}>3</span>
            <div className="kb-rule-body">
              <strong>Strategy Modules &amp; Event Bridge</strong><br />
              Strategies (e.g., <code>rsi-straddle.js</code>) are implemented as isolated Node.js EventEmitter classes. They receive live ticks from the Shared Apollo socket via an internal event bus. When a strategy generates a signal, it emits an <code>order</code> event. The <code>server.js</code> execution bridge catches this event, applies a rate limiter (8 orders/sec), and pushes it to the broker.
            </div>
          </div>

          <div className="kb-rule">
            <span className="kb-rule-num" style={{ background: '#be123c', color: 'white' }}>4</span>
            <div className="kb-rule-body">
              <strong>3-Layer Risk Management System</strong><br />
              <ol style={{ paddingLeft: '20px', marginTop: '10px' }}>
                <li><strong>Trade-level:</strong> Managed strictly inside the strategy logic (e.g., 100% premium SL).</li>
                <li><strong>Strategy-level (2%):</strong> The VPS <code>RiskPoller</code> runs every 2 seconds, checking if a specific strategy's MTM loss exceeds 2% of its allocated margin. If so, it squares off that specific strategy.</li>
                <li><strong>Global Circuit Breaker (3%):</strong> The VPS checks the aggregate PnL across ALL running strategies against the global firm margin. If the 3% loss limit is hit, it squares off everything, blocks all new entry orders, and alerts the Cloudflare Worker API (which then pushes to Telegram).</li>
              </ol>
            </div>
          </div>

          <div className="kb-rule">
            <span className="kb-rule-num" style={{ background: '#ea580c', color: 'white' }}>5</span>
            <div className="kb-rule-body">
              <strong>Real-time State Synchronization (SSE &amp; WS)</strong><br />
              To prevent the React frontend from constantly polling the database, the system uses two push technologies:
              <ul>
                <li><strong>Server-Sent Events (SSE):</strong> The VPS streams execution logs, strategy state changes, and broker connection status directly to the browser via the Cloudflare Tunnel.</li>
                <li><strong>Durable Object WebSocket:</strong> The VPS pushes aggregated 1-second price snapshots to the Cloudflare Worker, which broadcasts them over WebSockets to all connected dashboard clients for live UI updates.</li>
              </ul>
            </div>
          </div>

        </div>
      </section>
      
      {/* Mermaid Initialization Script */}
      <script dangerouslySetInnerHTML={{ __html: `
        if (typeof mermaid !== 'undefined') {
          mermaid.init(undefined, document.querySelectorAll('.mermaid'));
        }
      `}} />
    </>
  )
}
